import React, { Suspense, PureComponent } from 'react'
import { connect } from 'react-redux'
import validator from 'validator'

import { Helmet } from 'react-helmet'

const PaymentDetails = React.lazy(() => import('./forms/PaymentDetails'))
const PaymentShipping = React.lazy(() => import('./forms/PaymentShipping'))
const PaymentMethod = React.lazy(() => import('./forms/PaymentMethod'))
const PaymentCheckout = React.lazy(() => import('./forms/PaymentCheckout'))
const PaymentConfirmation = React.lazy(() => import('./PaymentConfirmation'))
const PaymentItemDetails = React.lazy(() => import('./PaymentItemDetails'))
import { PaymentConstants } from '../paymentConstants'

const Flex = React.lazy(() => import('../components/Flex'))

const Modal = React.lazy(() => import('../components/modal/Modal'))
const ModalOverlay = React.lazy(() => import('../components/modal/ModalOverlay'))
const LeftModalPanel = React.lazy(() => import('../components/modal/panels/LeftModalPanel'))
const RightModalPanel = React.lazy(() => import('../components/modal/panels/RightModalPanel'))
const ModalCloseButton = React.lazy(() => import('../components/modal/ModalCloseButton'))

import {
    GOTO_ITEM_DETAILS_PAGE,
    GOTO_CHECKOUT_PAGE,
    GOTO_ORDER_COMPLETE_PAGE,
    GOTO_SHIPPING_ADDRESS_PAGE,
    GOTO_PAYMENT_METHOD_PAGE,
    changeFormField,
    blurFormField,
    setFormFieldInvalid,
    setBillingAddressSameAsShipping,
    productDetailsOpened,
    productDetailsClosed,
    gotoShippingAddressPage,
    gotoPaymentMethodPage,
    executePaymentTransaction,
    setPaymentPage,
    closePaymentsModal,
    setAuthPopup,
    closePaymentOAuthWindow,
    setIsUserAuthenticated,
    setIsUserEditingItemDetails,
    resetPaymentFormValues,
    setStripeProperties,
} from '../paymentActions'

const defaultFormFields = {
    shippingFields: [
        {
            id: 'fullName',
            placeholder: 'Full Name',
            required: true,
        },
        {
            id: 'address',
            placeholder: 'Address',
            required: true,
        },
        {
            id: 'zipcode',
            placeholder: 'Zipcode',
            required: true,
            validation: value => validator.isPostalCode(value, 'US'),
            errorMessage: 'Invalid US zip code!',
        },
        {
            id: 'town',
            placeholder: 'Town/City',
            required: true,
        },
        {
            id: 'country',
            placeholder: 'Country',
            disabled: true,
            required: true,
        },
        {
            id: 'state',
            placeholder: 'State',
            required: true,
        },
        {
            id: 'email',
            placeholder: 'Email',
            required: true,
            validation: value => validator.isEmail(value),
            errorMessage: 'Invalid email!',
        },
        {
            id: 'phone',
            placeholder: 'Phone (Optional)',
        },
    ],
}

const requiredFormFields = {
    shippingFields: ['fullName', 'address', 'zipcode', 'town', 'country', 'state', 'email', 'phone'],
}

class PaymentContainer extends PureComponent {
    constructor(props) {
        super(props)

        //TODO
        //Move this logic into whoever is importing this
        //payment container. If the code is left here
        //we run the risk of never getting the chance
        //to bind this to the window when the tour loads
        window.onpopstate = this.loadNewState
    }

    loadNewState = event => {
        //if we are already at complete page, just exit the modal
        if (this.isCurrentPage(GOTO_ORDER_COMPLETE_PAGE)) {
            this.props.closePaymentsModal()
            return
        }
        //Get the next page name from the state
        const { paymentPageName } = event.state || {}
        const nextPageName = paymentPageName || GOTO_ITEM_DETAILS_PAGE

        //Get the current page we're on
        const { paymentPageName: previousPageName, setPaymentPage } = this.props

        //Check if the page needs to change
        if (previousPageName != nextPageName) {
            let goToPageName = nextPageName
            if ([GOTO_PAYMENT_METHOD_PAGE, GOTO_CHECKOUT_PAGE].includes(nextPageName)) {
                if (goToPageName === GOTO_CHECKOUT_PAGE) {
                    goToPageName = GOTO_PAYMENT_METHOD_PAGE
                }
                this.props.resetPaymentFormValues({ formName: PaymentConstants.formNames.STRIPE_PAYMENT_METHOD })
                //reset the stripe properties to its initial state
                this.props.setStripeProperties({
                    stripeProperties: {
                        shouldSubmit: false,
                        cardName: '',
                    },
                })
            }

            setPaymentPage({
                pageName: goToPageName,
                fromHistory: true,
            })
        }
    }

    isCurrentPage = pageName => {
        return pageName == this.props.paymentPageName
    }

    componentDidMount() {
        this.props.productDetailsOpened(this.props)
    }

    componentWillUnmount() {
        this.props.productDetailsClosed()
    }

    setAuthPopupVisible = () => {
        this.props.setAuthPopup({ isVisible: true })
    }

    onOverlayClick = () => {
        if (this.props.isUserAuthPopUpVisible) {
            this.props.setAuthPopup({ isVisible: false })
        }
    }

    handlePaymentDetailsSubmit = () => {
        if (this.props.isUserEditingItemDetails) {
            this.props.setIsUserEditingItemDetails({ isEditing: false })
        } else {
            this.props.isUserAuthenticated ? this.props.gotoShippingAddressPage() : this.setAuthPopupVisible()
        }
    }

    render() {
        const {
            platform,
            gotoPaymentMethodPage,
            postPaymentMethodInfo,
            executePaymentTransaction,
            closePaymentsModal,
            isUserAuthenticated,
            isFormProcessing,
            changeFormField,
            setFormFieldInvalid,
            blurFormField,
            isMobile,
        } = this.props

        const isDetailsPage = this.isCurrentPage(GOTO_ITEM_DETAILS_PAGE)
        const isCheckoutPage = this.isCurrentPage(GOTO_CHECKOUT_PAGE)
        const isOrderCompletePage = this.isCurrentPage(GOTO_ORDER_COMPLETE_PAGE)
        const isShippingPage = this.isCurrentPage(GOTO_SHIPPING_ADDRESS_PAGE)
        const isPaymentMethodPage = this.isCurrentPage(GOTO_PAYMENT_METHOD_PAGE)

        //All the testing components
        return (
            <Suspense fallback={<div>Loading...</div>}>
                <React.Fragment>
                    <Helmet>
                        <script src="https://js.stripe.com/v3/" />
                    </Helmet>
                    <Modal
                        isMobile={isMobile}
                        onCloseClick={closePaymentsModal}
                        onClick={isDetailsPage && this.onOverlayClick}
                        isCloseButtonVisible={!isMobile}
                    >
                        {isMobile && isDetailsPage && (
                            <ModalCloseButton
                                isMobile={isMobile}
                                isDetailsPage={isDetailsPage}
                                onClick={closePaymentsModal}
                            />
                        )}

                        {//Show the complete page
                        isOrderCompletePage ? (
                            <PaymentConfirmation isMobile={isMobile} onClick={closePaymentsModal} />
                        ) : !isOrderCompletePage && isMobile ? (
                            <div>
                                <ModalOverlay
                                    isMobile={isMobile}
                                    isVisible={isFormProcessing}
                                    backgroundColor={isDetailsPage ? 'transparent' : '#fff'}
                                />

                                <PaymentItemDetails
                                    isCheckoutPage={isCheckoutPage}
                                    isMobile={isMobile}
                                    showItemSummary={!isDetailsPage}
                                />

                                {isMobile && !isDetailsPage && (
                                    <ModalCloseButton
                                        isMobile={isMobile}
                                        isDetailsPage={isDetailsPage}
                                        onClick={closePaymentsModal}
                                    />
                                )}
                                {isDetailsPage && (
                                    <PaymentDetails
                                        isMobile={isMobile}
                                        isUserAuthenticated={isUserAuthenticated}
                                        onSubmit={this.handlePaymentDetailsSubmit}
                                    />
                                )}
                                {isShippingPage && (
                                    <PaymentShipping
                                        isMobile={isMobile}
                                        onFieldChange={changeFormField}
                                        onFieldBlur={blurFormField}
                                        onSubmit={gotoPaymentMethodPage}
                                        defaultFields={{
                                            shippingFields: defaultFormFields.shippingFields,
                                        }}
                                    />
                                )}
                                {isPaymentMethodPage && (
                                    <PaymentMethod
                                        isMobile={isMobile}
                                        onFieldChange={changeFormField}
                                        onFieldError={setFormFieldInvalid}
                                        defaultFields={{
                                            paymentFields: defaultFormFields.paymentFields,
                                            shippingFields: defaultFormFields.shippingFields,
                                        }}
                                        requiredFields={{
                                            paymentFields: requiredFormFields.paymentFields,
                                            shippingFields: requiredFormFields.shippingFields,
                                        }}
                                    />
                                )}
                                {isCheckoutPage && (
                                    <PaymentCheckout
                                        isMobile={isMobile}
                                        onFieldChange={changeFormField}
                                        onFieldBlur={blurFormField}
                                        defaultFields={{
                                            shippingFields: defaultFormFields.shippingFields,
                                        }}
                                        requiredFields={{
                                            paymentFields: requiredFormFields.paymentFields,
                                        }}
                                        onSubmit={executePaymentTransaction}
                                    />
                                )}
                            </div>
                        ) : (
                            <Flex>
                                <LeftModalPanel isMobile={isMobile} isDetailsPage={isDetailsPage}>
                                    <ModalOverlay
                                        isMobile={isMobile}
                                        isVisible={isFormProcessing}
                                        backgroundColor={isDetailsPage ? 'transparent' : '#fff'}
                                    />

                                    <PaymentItemDetails
                                        isCheckoutPage={isCheckoutPage}
                                        isMobile={isMobile}
                                        showItemSummary={!isDetailsPage}
                                    />
                                </LeftModalPanel>

                                <RightModalPanel
                                    isMobile={isMobile}
                                    isDetailsPage={isDetailsPage}
                                    isCheckoutPage={isCheckoutPage}
                                >
                                    <ModalOverlay
                                        isMobile={isMobile}
                                        isVisible={isFormProcessing}
                                        backgroundColor={isDetailsPage ? 'transparent' : '#fff'}
                                    />
                                    {isMobile && !isDetailsPage && (
                                        <ModalCloseButton
                                            isMobile={isMobile}
                                            isDetailsPage={isDetailsPage}
                                            onClick={closePaymentsModal}
                                        />
                                    )}
                                    {isDetailsPage && (
                                        <PaymentDetails
                                            isMobile={isMobile}
                                            isUserAuthenticated={isUserAuthenticated}
                                            onSubmit={this.handlePaymentDetailsSubmit}
                                        />
                                    )}
                                    {isShippingPage && (
                                        <PaymentShipping
                                            isMobile={isMobile}
                                            onFieldChange={changeFormField}
                                            onFieldBlur={blurFormField}
                                            onSubmit={gotoPaymentMethodPage}
                                            defaultFields={{
                                                shippingFields: defaultFormFields.shippingFields,
                                            }}
                                        />
                                    )}
                                    {isPaymentMethodPage && (
                                        <PaymentMethod
                                            isMobile={isMobile}
                                            onFieldChange={changeFormField}
                                            onFieldError={setFormFieldInvalid}
                                            defaultFields={{
                                                paymentFields: defaultFormFields.paymentFields,
                                                shippingFields: defaultFormFields.shippingFields,
                                            }}
                                            requiredFields={{
                                                paymentFields: requiredFormFields.paymentFields,
                                                shippingFields: requiredFormFields.shippingFields,
                                            }}
                                        />
                                    )}
                                    {isCheckoutPage && (
                                        <PaymentCheckout
                                            isMobile={isMobile}
                                            onFieldChange={changeFormField}
                                            onFieldBlur={blurFormField}
                                            defaultFields={{
                                                shippingFields: defaultFormFields.shippingFields,
                                            }}
                                            requiredFields={{
                                                paymentFields: requiredFormFields.paymentFields,
                                            }}
                                            onSubmit={executePaymentTransaction}
                                        />
                                    )}
                                </RightModalPanel>
                            </Flex>
                        )}
                    </Modal>
                </React.Fragment>
            </Suspense>
        )
    }
}

export default connect(
    state => {
        const isFormProcessing =
            state.getIn(['payments', PaymentConstants.formNames.SHIPPING_ADDRESS, 'isFormProcessing']) ||
            state.getIn(['payments', PaymentConstants.formNames.STRIPE_PAYMENT_METHOD, 'isFormProcessing']) ||
            state.getIn(['payments', PaymentConstants.formNames.CHECKOUT, 'isFormProcessing'])

        return {
            paymentPageName: state.getIn(['payments', 'paymentPageName']),
            isMobile: state.getIn(['device', 'isDeviceMobile']),
            isUserAuthPopUpVisible: state.getIn(['payments', 'isUserAuthPopUpVisible']),
            isUserAuthenticated: state.getIn(['payments', 'isUserAuthenticated']),
            OAuthWindowStatus: state.getIn(['payments', 'OAuthWindowStatus']),
            isUserEditingItemDetails: state.getIn(['payments', 'isUserEditingItemDetails']),
            isFormProcessing,
        }
    },
    {
        changeFormField,
        blurFormField,
        setFormFieldInvalid,
        setBillingAddressSameAsShipping,
        gotoShippingAddressPage,
        gotoPaymentMethodPage,
        executePaymentTransaction,
        productDetailsOpened,
        productDetailsClosed,
        setPaymentPage,
        closePaymentsModal,
        setAuthPopup,
        closePaymentOAuthWindow,
        setIsUserAuthenticated,
        setIsUserEditingItemDetails,
        resetPaymentFormValues,
        setStripeProperties,
    }
)(PaymentContainer)
